/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import type { TranslatorProps } from 'react-i18next';
import RequireForAccess from 'react-access';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';

import navigation from 'services/NavigationService';
import Loading from 'components/Loading/Loading';
import DashboardStore from 'stores/DashboardStore';
import { createSingleOwnedAsset } from 'permissions/asset.js';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { isIE } from 'utils/browserDetection';

import ChartLegend from '../../components/ChartLegend/ChartLegend';
import WidgetActions from '../../components/WidgetActions';
import InsufficientData from '../../components/InsufficientData/InsufficientData';
import NivoLineChart from '../../components/NivoLineChart';
import type { CommonWidgetProps } from '../../commonTypes';

import styles from './AssetsByTypeBar.module.scss';
import NavigationService from 'services/NavigationService';

const STATIC_DATA = [
  {
    rented: 22,
    owned: 123,
    consignment: 64,
    subrental: 58,
  },
];

const ownedAssetServiceMap = {
  consigned: 'Consignment',
  subrent: 'Subrental',
};

const assetTypeMap = {
  consigned: ['Owned'],
  subrent: ['Owned'],
  owned: ['Owned'],
  rented: ['Rented Open', 'Purchased'],
};

export type AssetsByTypeBarProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

type State = {
  data: { id: string, color: string, data: { x: string, y: number }[] }[],
  overtime: boolean,
};

@translate()
@inject('dashboardStore')
@observer
export default class AssetsByTypeBar extends ComponentWithInjectedProps<
  AssetsByTypeBarProps,
  State,
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || AssetsByTypeBar.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): { overtime: any[] | null, assets: any[] | null } {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return {
        assets: STATIC_DATA,
        overtime: null,
      };
    }

    return {
      overtime: dashboardStore.overtime ? dashboardStore.overtime['assets-over-time'] : null,
      assets: dashboardStore.assets ? dashboardStore.assets.assets : null,
    };
  }

  state = {
    data: [
      {
        id: 'Active',
        color: 'hsl(12, 74%, 59%)',
        data: [
          {
            x: 'Jan 1',
            y: 0,
          },
        ],
      },
    ],
    overtime: false,
  };

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  async loadWidgetData(tradingAccount: string) {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      await dashboardStore.loadAssets(tradingAccount);

      this.changeData();
    }
  }

  async changeData() {
    const dashboardStore = this.props.dashboardStore;
    await dashboardStore.loadAssetsOverTime(dashboardStore.loadedTradingAccount);

    const dates = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    const time = new Date();
    const year = time.getFullYear();
    const rented = [];
    const owned = [];
    const consignment = [];
    const subrental = [];

    /* eslint-disable max-nested-callbacks */
    dates.map(date => {
      const monthArray = this.widgetData.overtime
        ? this.widgetData.overtime.filter(e => e.month === date).filter(e => e.year === year)
        : null;

      const dateRented = {
        x: date + '/' + year,
        y: monthArray && monthArray[0] ? monthArray[0].rented : 0,
      };

      const dateOwned = {
        x: date + '/' + year,
        y: monthArray && monthArray[0] ? monthArray[0].owned : 0,
      };

      const dateConsignment = {
        x: date + '/' + year,
        y: monthArray && monthArray[0] ? monthArray[0].consignment : 0,
      };

      const dateSubrental = {
        x: date + '/' + year,
        y: monthArray && monthArray[0] ? monthArray[0].subrental : 0,
      };

      rented.push(dateRented);
      owned.push(dateOwned);
      consignment.push(dateConsignment);
      subrental.push(dateSubrental);

      return rented;
    });
    /* eslint-enable max-nested-callbacks */

    const assetLineData = [
      {
        id: 'Rented',
        color: 'hsl(199, 77%, 41%)',
        data: rented,
      },
      {
        id: 'Subrent',
        color: 'hsl(197, 8%, 67%)',
        data: subrental,
      },
      {
        id: 'Consiged',
        color: 'hsl(198, 15%, 34%)',
        data: consignment,
      },
      {
        id: 'Owned',
        color: 'hsl(9, 87%, 61%)',
        data: owned,
      },
    ];

    this.setState({
      data: assetLineData,
    });
  }

  handleClick() {
    this.setState({
      overtime: !this.state.overtime,
    });
  }

  render() {
    const { dashboardStore, t, widgetItem, size, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;

    const assetBarData = [
      {
        label: 'Asset',
        Rented: this.widgetData.assets
          ? this.widgetData.assets[0].rented
          : size === 'thumbnail'
          ? 1
          : 0,
        RentedColor: 'hsl(199, 77%, 41%)',
        Subrent: this.widgetData.assets
          ? this.widgetData.assets[0].subrental
          : size === 'thumbnail'
          ? 2
          : 0,
        SubrentColor: 'hsl(197, 8%, 67%)',
        Consigned: this.widgetData.assets
          ? this.widgetData.assets[0].consignment
          : size === 'thumbnail'
          ? 3
          : 0,
        ConsignedColor: 'hsl(198, 15%, 34%)',
        Owned: this.widgetData.assets
          ? this.widgetData.assets[0].owned
          : size === 'thumbnail'
          ? 4
          : 0,
        OwnedColor: 'hsl(9, 87%, 61%)',
      },
    ];

    const isInteractive = size !== 'thumbnail';

    const barMargins =
      size === 'thumbnail'
        ? { top: 0, right: 0, bottom: 0, left: 0 }
        : { top: 0, right: 0, bottom: 50, left: 5 };

    return (
      <Card className={styles[size]}>
        <CardHeader
          title={t('general:dashboards.assets_by_type')}
          action={<WidgetActions widgetItem={widgetItem} size={size} isFullScreen={isFullScreen} />}
          className={styles['card-header']}
          classes={{ action: styles.cardHeaderAction }}
        />
        {(!isIE || size !== 'thumbnail') && (
          <React.Fragment>
            <Divider />
            <CardContent className={styles['card-content']}>
              {(this.widgetData.assets &&
                (this.widgetData.assets[0].rented > 0 ||
                  this.widgetData.assets[0].subrental > 0 ||
                  this.widgetData.assets[0].consignment > 0 ||
                  this.widgetData.assets[0].owned > 0)) ||
              (this.shouldLoadData && dashboardStore.loadingAssets) ? (
                this.shouldLoadData &&
                (dashboardStore.loadingAssets || dashboardStore.loadingAssetsOverTime) ? (
                  <Loading />
                ) : (
                  <React.Fragment>
                    {size === 'thumbnail' ? null : (
                      <ChartLegend
                        position="center"
                        keys={[
                          {
                            label: t('general:assets.owned_asset_plural'),
                            color: 'hsl(9, 87%, 61%)',
                          },
                          {
                            label: t('general:assets.consigned_asset_plural'),
                            color: 'hsl(198, 15%, 34%)',
                          },
                          {
                            label: t('general:assets.subrented_asset_plural'),
                            color: 'hsl(197, 8%, 67%)',
                          },
                          {
                            label: t('general:assets.rented_asset_plural'),
                            color: 'hsl(199, 77%, 41%)',
                          },
                        ]}
                      />
                    )}
                    <div className={styles['full-container']}>
                      <div
                        className={`${styles['half-container']} ${
                          this.state.overtime ? '' : styles['display-con']
                        }`}
                      >
                        <ResponsiveBar
                          data={assetBarData}
                          keys={['Owned', 'Consigned', 'Subrent', 'Rented']}
                          margin={barMargins}
                          indexBy="label"
                          innerPadding={5}
                          padding={0}
                          layout="horizontal"
                          groupMode="grouped"
                          onClick={e => {
                            const assetType = e.id.toLowerCase();

                            navigation.navigateToReportView('/assets', {
                              page: 1,
                              filters: [
                                ...(accountId ? [{ key: 'account', values: [accountId] }] : []),
                                ...(['consigned', 'subrent'].includes(assetType)
                                  ? [
                                      {
                                        key: 'owned_asset_service',
                                        values: [ownedAssetServiceMap[assetType]],
                                      },
                                    ]
                                  : []),
                                {
                                  key: 'asset_type',
                                  values: assetTypeMap[assetType],
                                },
                              ],
                            });
                          }}
                          colorBy={function(e: any): string {
                            return e.data[e.id + 'Color'];
                          }}
                          axisLeft={null}
                          axisBottom={{
                            orient: 'bottom',
                            tickSize: 0,
                            tickPadding: 10,
                            tickRotation: -90,
                          }}
                          enableGridX={isInteractive}
                          enableGridY={false}
                          labelSkipWidth={20}
                          labelSkipHeight={12}
                          labelTextColor="#ffffff"
                          animate={true}
                          motionStiffness={90}
                          motionDamping={15}
                          enableLabel={isInteractive}
                          isInteractive={isInteractive}
                        />
                      </div>
                      {size !== 'thumbnail' && (
                        <div
                          className={`${styles['half-container']} ${
                            this.state.overtime ? styles['display-con'] : ''
                          }`}
                        >
                          <NivoLineChart data={this.state.data} widgetSize={size} />
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                )
              ) : (
                <InsufficientData icon={size !== 'thumbnail'} />
              )}
            </CardContent>
          </React.Fragment>
        )}
        {isInteractive && (
          <div className={styles['button-container']}>
            {size === 'canvas-50' ? (
              <Button
                color="secondary"
                variant="contained"
                onClick={(e: any) => this.handleClick()}
              >
                {this.state.overtime ? t('general:overall') : t('general:overtime')}
              </Button>
            ) : null}
            <RequireForAccess permissions={createSingleOwnedAsset}>
              <Link
                to={NavigationService.formulateReportViewUri('/assets', {
                  page: 1,
                  filters: [...(accountId ? [{ key: 'account', values: [accountId] }] : [])],
                })}
              >
                <Button color="primary" variant="contained">
                  {t('general:actions.add_new_asset')}
                </Button>
              </Link>
            </RequireForAccess>
          </div>
        )}
      </Card>
    );
  }
}
